import type { StorageAdapter } from '@mtm/auth';

/**
 * localStorage-backed storage adapter that gives the IAM SDK its own slot for the keys that
 * collide with the legacy app's storage. Only `auth_user` is remapped: the SDK persists its
 * IAM identity there (which has `email`/`firstName`/`lastName` but no legacy `username`/
 * `companyId`/`userPermissionType`/`brandId`/`fullName`), overwriting the legacy
 * gateway user that the app, route guards, and the conversation/task SDKs all read. Remapping that
 * key isolates the two sessions, so the SDK can never clobber `localStorage['auth_user']` —
 * `loadUser`'s `setAuthUser()` write is then the single source of truth for it.
 *
 * `app_token`/`app_token_expiry` are deliberately NOT remapped: the legacy app was renamed from
 * `token` to `app_token` (APP_TOKEN_STORAGE_KEY) to share ONE token slot with the SDK, matching
 * core-fe. The legacy code therefore always reads the SDK's live (renewed) token.
 *
 * The SDK only wires cross-tab `onStorageChange`/`destroy` onto its own default adapter (see
 * `MtmAuthClient`: `_ownedStorageAdapter` is null when a custom adapter is supplied), so a provided
 * adapter is used purely through these five `StorageAdapter` methods.
 */
const REMAPPED_KEYS: Readonly<Record<string, string>> = {
  auth_user: 'iam_auth_user',
};

const mapKey = (key: string): string => REMAPPED_KEYS[key] ?? key;

export class MtmIamStorageAdapter implements StorageAdapter {
  getItem(key: string): string | null {
    return localStorage.getItem(mapKey(key));
  }

  getItemSync(key: string): string | null {
    return localStorage.getItem(mapKey(key));
  }

  setItem(key: string, value: string): void {
    localStorage.setItem(mapKey(key), value);
  }

  removeItem(key: string): void {
    localStorage.removeItem(mapKey(key));
  }

  clear(): void {
    // Only drop the SDK's isolated slot(s) — never wipe the legacy app's keys.
    Object.values(REMAPPED_KEYS).forEach((k) => localStorage.removeItem(k));
  }
}
